// export default HomePage;
import ProductCard from '@/components/common/ProductCard';
import TestimonialCard from '@/components/common/TestimonialCard';
import { useCategories } from '@/hooks/useCategories';
import { useBooksByCategory, useFeaturedBooks } from '@/hooks/useProducts';
import { ArrowRight, Sparkles, BookOpen, TrendingUp, Truck, Package, Shield } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTestimonials } from '@/hooks/useTestimonial';
import GallerySection from '@/components/common/GallerySection';
import HeroImage from '@/assets/hero3.png';
import Gallery1 from '@/assets/1.webp';
import Gallery2 from '@/assets/2.webp';
import Gallery3 from '@/assets/3.webp';
import Gallery4 from '@/assets/4.webp';
import Placeholder from '@/assets/placeholder.webp';
import { AppDownloadBanner } from '@/components/AppDownloadBanner';
import { useEffect } from 'react';


import WhyChooseUs from './WhyChooseUs';
import { useAuth } from '@/contexts/AuthContext';

// Home Page
const HomePage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
const { data: featuredBooks, isLoading: booksLoading  } = useFeaturedBooks(8);

const { data: ceramicAcademyBooks, isLoading: ceramicAcademyLoading } =   useBooksByCategory('ceramic-academy', undefined, 8);

const { data: currentAffairsBooks, isLoading: currentAffairsLoading } =  useBooksByCategory('current-affairs', undefined, 8);

const { data: categories, isLoading: categoriesLoading} =  useCategories(8);

const { data: testimonialList, isLoading: testimonialsLoading } =  useTestimonials();
  const { user } = useAuth();

  const layoutPattern = [
    'col-span-1', // featured large
    'col-span-1',
    'col-span-1',
    'col-span-1',
    'col-span-1',
    'col-span-1', // another featured
    'col-span-1',
    'col-span-1',
  ];


  return (
    <div className="bg-background">
      <Helmet>
        <title>Bookstore — Discover & Buy Books Online</title>
        <meta
          name="description"
          content="Find your next favorite book. Explore genres, discover stories, and buy books online with fast delivery and trusted quality."
        />
      </Helmet>

      {/* another hero section */}

      <div className="bg-background">
        <section className="bg-[#FCF8F1] bg-opacity-30 py-6 sm:py-10 lg:py-16">
          <div className="px-4 mx-auto container-custom sm:px-6 lg:px-8">
            <div className="grid items-center grid-cols-1 gap-12 lg:grid-cols-2">
              <div>
                <p className="text-sm font-semibold tracking-wider text-primary-800 uppercase">
                  Welcome to Sunrise Bookstore
                </p>
                <h1 className="mt-3 text-3xl font-bold text-primary lg:mt-5 sm:text-4xl xl:text-5xl">
                  Your Gateway to Endless Stories
                </h1>
                <p className="mt-3 text-base text-primary-800 lg:mt-5 sm:text-lg">
                  Discover a world of knowledge and inspiration at your fingertips.
                </p>

                <a
                  href="/products"
                  title=""
                  className="inline-flex items-center px-6 py-4 mt-8 font-semibold text-primary-800 transition-all duration-200 bg-primary-light rounded-md lg:mt-16 hover:bg-primary hover:text-white focus:bg-primary-200 transition"
                  role="button"
                >
                  Get Started
                  <svg
                    className="w-6 h-6 ml-8 -mr-2"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </a>

                {!user ? (
                  <p className="mt-5 text-primary-600">
                    Already joined us?{' '}
                    <a
                      href="#"
                      title=""
                      className="text-primary-900 transition-all duration-200 hover:underline"
                    >
                      Log in
                    </a>
                  </p>
                ) : (
                  <></>
                )}
              </div>

              <div>
                <img className="w-[500]" src={HeroImage}  width="500" height="200" alt="Sunrise Bookstore Hero Image" />
              </div>
            </div>
          </div>
        </section>
      </div>


      {/* 🎨 CATEGORIES SECTION */}

      <section className="py-24 bg-background-muted relative">
        <div className="w-full max-w-7xl px-6 lg:px-8 mx-auto">
          <div className="flex items-center justify-center flex-col gap-5 mb-14 text-center">
            <h2 className="font-manrope font-bold text-4xl text-primary">Explore by Category</h2>
            <p className="text-lg font-normal text-primary-800 max-w-3xl mx-auto">
              Discover a world of knowledge and inspiration across diverse categories.
            </p>
          </div>

          {categoriesLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8 mb-14">
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className={`h-60 bg-primary-200 rounded-2xl animate-pulse ${
                    layoutPattern[i] || ''
                  }`}
                ></div>
              ))}
            </div>
          ) : (
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{
                hidden: { opacity: 0, y: 40 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.6, staggerChildren: 0.1 },
                },
              }}
              className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8 mb-14"
            >
              {categories?.slice(0, 8).map((category, i) => (
                <motion.div
                  key={category.id}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0 },
                  }}
                  whileHover={{ scale: 1.03 }}
                  transition={{ duration: 0.3 }}
                  className={`${layoutPattern[i] || ''}`}
                >
                  <Link to={`/products/${category.seoUrl}`} className="block group" aria-label={`category-${category.seoUrl}`}>
                    <div
                      className="bg-contain bg-no-repeat  bg-center rounded-lg flex flex-col justify-end px-7 py-6 h-60 sm:h-80 text-white transition-all duration-300 hover:brightness-110"
                      style={{
                       
                        backgroundImage: `url(${
                          category.imageUrl ||
                          (Placeholder || 'https://images.unsplash.com/photo-1507842217343-583bb7270b66?auto=format&fit=crop&w=800&q=80')
                        })`,

                        // overlay behind text
                        boxShadow: 'inset 0 0 0 1000px rgba(0, 0, 0, 0.6)',
                      }}
                    >          
                      <h6 className="font-medium text-xl leading-8 mb-2">{category.name}</h6>
                      <p className="text-base font-normal text-white/80">
                        {category.description || 'Discover more in this category'}
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}

          <Link to="/categories" aria-label="categories">
            <div className="w-full rounded-lg py-4 px-6 text-center bg-primary text-lg font-medium text-white transition-all duration-300 hover:text-white hover:bg-primary-dark">
              View All Categories
            </div>
          </Link>
        </div>
      </section>

      {/* 📚 FEATURED BOOKS */}
      <section className="py-24 bg-background">
        <div className="container-custom text-center mb-16">
          <h2 className="text-4xl font-display font-bold text-primary mb-3">Featured Books</h2>
          <p className="text-primary-800 max-w-2xl mx-auto">
            Handpicked bestsellers and hidden gems you’ll love.
          </p>
        </div>

        <div className="container-custom">
          {booksLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-96 bg-primary-200 rounded-xl animate-pulse"></div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8"
            >
              {featuredBooks?.map((book) => (
                <ProductCard key={book.id} product={book} />
              ))}
            </motion.div>
          )}

          <div className="text-center mt-12">
            <Link
              to="/products"
              className="inline-flex items-center bg-primary text-white rounded-md px-6 py-3 hover:bg-primary-700 font-semibold transition shadow-sm"
            >
              View All Books
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* ceramic academy added */}
      <section className="py-24 bg-background-soft">
        <div className="container-custom text-center mb-16">
          <h2 className="text-4xl font-display font-bold text-primary mb-3">Ceramic Academy</h2>
          <p className="text-primary-800 max-w-2xl mx-auto">
              {/* ceramic academy */}
            Explore our curated selection of Ceramic Academy Books.
          </p>
        </div>
        <div className="container-custom">
          {ceramicAcademyLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {ceramicAcademyBooks?.map((_, i) => (
                <div key={i} className="h-96 bg-primary-200 rounded-xl animate-pulse"></div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8"
            >
              {ceramicAcademyBooks?.map((book) => (
                <ProductCard key={book.id} product={book} />
              ))}
            </motion.div>
          )}
          <div className="text-center mt-12">
            <Link
              to="/products/ceramic-academy"
              className="inline-flex items-center bg-primary text-white rounded-md px-6 py-3 hover:bg-primary-700 font-semibold transition shadow-sm"
            >
              See More Bestsellers
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>
      {/* entrance exam books */}
      <section className="py-24 bg-background">
        <div className="container-custom text-center mb-16">
          <h2 className="text-4xl font-display font-bold text-primary mb-3">Current Affairs</h2>
          <p className="text-primary-800 max-w-2xl mx-auto">
            Prepare effectively with our top Current Affairs Books selection.
          </p>
        </div>
        <div className="container-custom">
          {currentAffairsLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {currentAffairsBooks?.map((_, i) => (
                <div key={i} className="h-96 bg-primary-200 rounded-xl animate-pulse"></div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8"
            >
              {currentAffairsBooks?.map((book) => (
                <ProductCard key={book.id} product={book} />
              ))}
            </motion.div>
          )}
          <div className="text-center mt-12">
            <Link
              to="/products/current-affairs"
              className="inline-flex items-center bg-primary text-white rounded-md px-6 py-3 hover:bg-primary-700 font-semibold transition shadow-sm"
            >
              Explore New Arrivals
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>



      {/* 🎁 DEALS & DISCOUNTS */}
      <section className="py-24 bg-background-muted">
        <div className="container-custom mb-12 text-center">
          <h2 className="text-4xl font-display font-bold text-primary mb-3">Deals & Discounts</h2>
          <p className="text-text-secondary max-w-xl mx-auto">
            Great books at irresistible prices — grab them while they last.
          </p>
        </div>

        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {featuredBooks?.map((book) => (
              <ProductCard key={book.id} product={book} />
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/products?discounts=true"
              className="inline-flex items-center px-7 py-3 rounded-md bg-primary-600 text-white hover:bg-primary-700 font-semibold shadow-sm"
            >
              View All Deals
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* new thing added ended */}

      {/* 🪄 Banner SECTION */}
      <section className="relative overflow-hidden rounded-3xl mx-6 bg-background-deep">
        {/* Subtle paper texture */}
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23n)' opacity='0.9'/%3E%3C/svg%3E")`,
          }}
        />

        {/* Ambient Warm Glows */}
        <div className="absolute top-[-200px] left-[-100px] w-[600px] h-[600px] bg-primary-50 rounded-full blur-3xl opacity-60"></div>
        <div className="absolute bottom-[-200px] right-[-150px] w-[500px] h-[500px] bg-primary-light rounded-full blur-[160px]"></div>

        <div className="container-custom relative z-10 py-24 lg:py-32 grid md:grid-cols-2 gap-16 items-center">
          {/* TEXT BLOCK */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-text"
          >
            <h1 className="text-[48px] lg:text-[56px] font-light italic tracking-wide leading-[1.1] text-text mb-6">
              Discover Your Next
              <span className="block text-primary-600 font-normal not-italic">Great Read</span>
            </h1>

            <p className="text-[16px] leading-relaxed text-text-secondary max-w-lg mb-10">
              Browse, shop, and fall in love with timeless stories. Curated selections, premium
              prints, and a cozy literary experience from a bookstore that feels like home.
            </p>

            {/* BUTTONS */}
            <div className="flex flex-wrap gap-4">
              <Link
                to="/products"
                className="
            inline-flex items-center px-7 py-3 rounded-md
            bg-primary-600 text-white tracking-wide text-[16px]
            transition-all duration-200
            hover:bg-primary-700 hover:scale-[1.02]
            active:scale-[0.98]
          "
              >
                Browse Books
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>

              <Link
                to="/products?featured=true"
                className="
            inline-flex items-center px-7 py-3 rounded-md border border-primary-light text-text bg-primary-100
            hover:bg-primary-100/40 hover:scale-[1.02] transition-all duration-200
            text-[16px]
          "
              >
                Featured Books
              </Link>
            </div>
          </motion.div>

          {/* FLOATING VISUAL CARD */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="hidden md:block relative"
          >
            {/* Soft glow */}
            <div className="absolute inset-0 bg-primary-50 rounded-3xl blur-2xl opacity-40"></div>

            <div
              className="
        relative backdrop-blur-xl bg-background-soft/60
        border border-primary-light rounded-3xl p-10 shadow-[0_0_0_1px_rgba(0,0,0,0.04)]
      "
            >
              <div className="flex flex-col gap-8">
                {/* Highlight Block */}
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-primary-600 rounded-full flex items-center justify-center">
                    <Sparkles className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="text-[14px] text-text-secondary">This Week’s Highlight</p>
                    <p className="text-[24px] text-text font-medium">Up to 40% Off</p>
                  </div>
                </div>

                {/* Feature Points */}
                <div className="flex flex-col gap-4 text-[14px] text-text-secondary">
                  <div className="flex items-center gap-3">
                    <BookOpen className="w-5 h-5 text-primary-700" />
                    <span>10,000+ Books Available</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-5 h-5 text-primary-700" />
                    <span>Free Delivery on Orders ₹500+</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 💬 TESTIMONIALS */}
      {!testimonialsLoading && (
        <section className="py-24 bg-background-muted overflow-hidden relative ">
          <div className="container-custom text-center mb-12">
            <h2 className="text-4xl font-display font-bold text-primary mb-3">
              What Our Readers Say
            </h2>
            <p className="text-primary-800 max-w-2xl mx-auto">
              Trusted by thousands of book lovers worldwide.
            </p>
          </div>

          <div className="relative w-full overflow-hidden">
            {/* Left Blur */}
            <div className="pointer-events-none absolute left-0 top-0 h-full w-32 z-20 bg-gradient-to-r from-background-muted via-background-muted/80 to-transparent"></div>

            {/* Right Blur */}
            <div className="pointer-events-none absolute right-0 top-0 h-full w-32 z-20 bg-gradient-to-l from-background-muted via-background-muted/80 to-transparent"></div>

            <div
              className={`${(testimonialList?.length || 0) > 3 ? 'animate-marquee  flex gap-6 w-max' : 'flex gap-6 justify-center  '}`}
            >
              {testimonialList?.map((t) => (
                <div key={t.id} className="w-[450px] shrink-0 h-[400px]">
                  <TestimonialCard testimonial={t} />
                </div>
              ))}

              {(testimonialList?.length || 0) > 3 &&
                testimonialList?.map((t) => (
                  <div key={`${t.id}-clone`} className="w-[450px] shrink-0">
                    <TestimonialCard testimonial={t} />
                  </div>
                ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-2 bg-background-soft">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl py-10 px-10 xl:py-16 xl:px-20 bg-background-soft flex items-center justify-between flex-col gap-16 lg:flex-row">
            <div className="w-full lg:w-60">
              <h2 className="font-manrope text-4xl font-bold text-primary mb-4 text-center lg:text-left">
                Our Stats
              </h2>
              <p className="text-sm text-gray-500 leading-6 text-center lg:text-left">
                Trusted by thousands of readers worldwide.
              </p>
            </div>
            <div className="w-full lg:w-4/5">
              <div className="flex flex-col flex-1 gap-10 lg:gap-0 lg:flex-row lg:justify-between">
                <div className="block">
                  <div className="font-manrope font-bold text-4xl text-primary-800 mb-3 text-center lg:text-left">
                    260+
                  </div>
                  <span className="text-gray-900 text-center block lg:text-left">
                    Books Available
                  </span>
                </div>
                <div className="block">
                  <div className="font-manrope font-bold text-4xl text-primary-800 mb-3 text-center lg:text-left">
                    975+
                  </div>
                  <span className="text-gray-900 text-center block lg:text-left">
                    Authors Collaborated
                  </span>
                </div>
                <div className="block">
                  <div className="font-manrope font-bold text-4xl text-primary-800 mb-3 text-center lg:text-left">
                    724+
                  </div>
                  <span className="text-gray-900 text-center block lg:text-left">
                    Happy Readers
                  </span>
                </div>
                <div className="block">
                  <div className="font-manrope font-bold text-4xl text-primary-800 mb-3 text-center lg:text-left">
                    89+
                  </div>
                  <span className="text-gray-900 text-center block lg:text-left">
                    {' '}
                    Cities Covered
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 🎥 GALLERY / VIDEO SECTION */}
      <section className="py-24">
        <div className="container-custom text-center mb-12">
          <h2 className="text-4xl font-display font-bold text-primary mb-3">Inside Our World</h2>
          <p className="text-primary-800 max-w-2xl mx-auto">
            A glimpse into the stories, authors, and books that shape us.
          </p>
        </div>

        <div className="container-custom grid md:grid-cols-2 gap-8">
          <div className="rounded-3xl overflow-hidden shadow-lg flex">
            <iframe
              className="w-full aspect-video rounded-3xl h-full"
              src="https://www.youtube.com/embed/jfKfPfyJRdk"
              title="Bookstore video"
              allowFullScreen
            ></iframe>

          </div>
          <div className="grid grid-cols-2 gap-6">
            {[Gallery1, Gallery2, Gallery3, Gallery4].map((src, i) => (
              <div
                key={i}
                className="overflow-hidden rounded-2xl shadow-md hover:scale-105 transition-transform"
              >
                <img src={src} alt="Gallery" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* gallery section */}
      <GallerySection />

      {/* why us  */}
      <WhyChooseUs
        cards={[
          {
            icon: <BookOpen width="30" height="30" stroke="#A8523A" strokeWidth="2" />,
            title: 'Vast Collection',
            description: 'From classics to modern favorites — everything under one roof.',
          },
          {
            icon: <Truck width="30" height="30" stroke="#A8523A" strokeWidth="2" />,
            title: 'Fast Shipping',
            description: 'Delivered to your doorstep quickly and safely.',
          },
          {
            icon: <Package width="30" height="30" stroke="#A8523A" strokeWidth="2" />,
            title: 'Fast Customer Service',
            description: '24/7 support and hassle-free returns make us your go-to store.',
          },
          {
            icon: <Shield width="30" height="30" stroke="#A8523A" strokeWidth="2" />,
            title: 'Quick Transaction',
            description: 'Secure and swift payment processing for a seamless shopping experience.',
          },
        ]}
      />
      <AppDownloadBanner />
    </div>
  );
};

export default HomePage;
